import React from 'react';

import {useI18n} from '@udemy/i18n';
import {RadioBlock} from '@udemy/react-form-components';

import styles from './tired-step.module.less';

export type TimeOptionId = 'tomorrow-morning' | 'tomorrow-evening' | 'in-2-days';

export interface TimeOption {
    id: TimeOptionId;
    label: string;
    confirmationText: string;
    daysOffset: number;
    hour: number;
}

export interface TiredStepTimeSelectionProps {
    timeOptions: TimeOption[];
    selectedTimeOptionId: TimeOptionId | null;
    onSelect: (id: TimeOptionId) => void;
    error: string | null;
}

export function TiredStepTimeSelection({
    timeOptions,
    selectedTimeOptionId,
    onSelect,
    error,
}: TiredStepTimeSelectionProps) {
    const {gettext} = useI18n();
    const selectedOption = timeOptions.find((o) => o.id === selectedTimeOptionId) ?? null;

    return (
        <div className={styles['content']}>
            <h2 className="ud-heading-xl">{gettext('Good work today.')}</h2>
            <p className="ud-text-md">
                {gettext(
                    'Coming back after a break actually helps it stick — spacing your learning out beats cramming it into one sitting.',
                )}
            </p>
            {error && (
                <p className={styles['error']} role="alert">
                    {error}
                </p>
            )}
            <p className="ud-heading-md">{gettext('When should I nudge you to come back?')}</p>
            <div
                role="radiogroup"
                aria-label={gettext('Select a reminder time')}
                className={styles['reminder-options']}
            >
                {timeOptions.map((option) => (
                    <RadioBlock
                        key={option.id}
                        name="reminder-time"
                        value={option.id}
                        checked={selectedTimeOptionId === option.id}
                        onChange={() => onSelect(option.id)}
                    >
                        {option.label}
                    </RadioBlock>
                ))}
            </div>
            {selectedOption && (
                <p className={styles['confirmation-text']}>{selectedOption.confirmationText}</p>
            )}
        </div>
    );
}
